import React, { useState, useEffect, useRef } from 'react';
import {
  getPosterListPaged,
  getFavoritePosters,
  deleteFavoritePoster,
  searchPosterDocTags,
  getRecentPosterDocTags,
} from '../firebase/api';
import { useNavigate, useLocation } from 'react-router-dom';
import './PosterList.css';
import UserHeader from './UserHeader';
import { useAuth } from '../firebase/AuthContext';
import { clearPosterListScrollPosition, scrollWindowToTop } from '../utils/scrollNavigation';

const LIST_PAGE_SIZE = 30;

/** 一覧「思考フェーズ」フィルタ（phase_tags.tag = 画像ファイル名拡張子なし・バックエンドと一致） */
const PHASE_FILTER_TAGS = [
  'reaction',
  'fact',
  'discovery',
  'grope',
  'reflection',
  'question',
  'analysis',
  'opinion',
  'outcome',
  'insight',
  'intention',
  'trial-and-error',
  'idea',
];

const orderPhaseSlugs = (slugs) => PHASE_FILTER_TAGS.filter((s) => slugs.includes(s));

/** 詳細画面などから戻ったときに一覧の絞り込みを復元する（タブを閉じるまで） */
const POSTER_LIST_SESSION_KEY = 'tinyIdentity_posterListFilters';

const splitYmdParts = (iso) => {
  if (!iso || typeof iso !== 'string') return ['', '', ''];
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return ['', '', ''];
  return [m[1], m[2], m[3]];
};

const normalizeListSubFilter = (sub, dates, tag, phases) => {
  let s = sub;
  if (s === 'date_range' && (!dates.from || !dates.to)) s = 'recent';
  if (s === 'tag' && !tag) s = 'recent';
  if (s === 'phase' && (!phases || !phases.length)) s = 'recent';
  return s;
};

const loadPosterListFiltersFromSession = (userId) => {
  if (typeof sessionStorage === 'undefined' || !userId) return null;
  try {
    const raw = sessionStorage.getItem(POSTER_LIST_SESSION_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw);
    if (!o || typeof o !== 'object' || o.userId !== userId) return null;
    const activeFilter = ['all', 'my', 'favorite'].includes(o.activeFilter) ? o.activeFilter : 'all';
    let listSubFilter = ['recent', 'date_range', 'tag', 'phase'].includes(o.listSubFilter)
      ? o.listSubFilter
      : 'recent';
    const appliedDateFrom = typeof o.appliedDateFrom === 'string' ? o.appliedDateFrom : '';
    const appliedDateTo = typeof o.appliedDateTo === 'string' ? o.appliedDateTo : '';
    const appliedTagExact = typeof o.appliedTagExact === 'string' ? o.appliedTagExact : '';
    const appliedPhaseTags = Array.isArray(o.appliedPhaseTags)
      ? orderPhaseSlugs(o.appliedPhaseTags.filter((x) => typeof x === 'string'))
      : [];
    const listPage = Number.isFinite(o.listPage) && o.listPage >= 1 ? Math.floor(o.listPage) : 1;
    listSubFilter = normalizeListSubFilter(listSubFilter, { from: appliedDateFrom, to: appliedDateTo }, appliedTagExact, appliedPhaseTags);
    return {
      activeFilter,
      listSubFilter,
      appliedDateFrom,
      appliedDateTo,
      appliedTagExact,
      appliedPhaseTags,
      listPage,
    };
  } catch {
    return null;
  }
};

const persistPosterListFiltersToSession = (userId, payload) => {
  if (typeof sessionStorage === 'undefined' || !userId) return;
  try {
    sessionStorage.setItem(
      POSTER_LIST_SESSION_KEY,
      JSON.stringify({ userId, ...payload })
    );
  } catch (_) {
    /* 容量超過等は無視 */
  }
};

const toYYYYMMDD = (y, m, d) => {
  const yi = parseInt(String(y).trim(), 10);
  const mi = parseInt(String(m).trim(), 10);
  const di = parseInt(String(d).trim(), 10);
  if (Number.isNaN(yi) || Number.isNaN(mi) || Number.isNaN(di)) return null;
  const dt = new Date(Date.UTC(yi, mi - 1, di));
  if (dt.getUTCFullYear() !== yi || dt.getUTCMonth() !== mi - 1 || dt.getUTCDate() !== di) return null;
  return `${yi}-${String(mi).padStart(2, '0')}-${String(di).padStart(2, '0')}`;
};

const filterBreadcrumbLabel = (filterKey) => {
  if (filterKey === 'all') return '全て';
  if (filterKey === 'my') return '自分の投稿';
  return 'お気に入り';
};

const PosterList = () => {
  const {
    userProfile,
    logout,
    handleSessionExpired,
    isSessionExpiredError,
    sessionId,
    currentUser,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  /** 同一マウント内で firebase uid に対して session からの復元を一度だけ行う（profile オブジェクト更新で再復元しない） */
  const listFiltersRestoreDoneUidRef = useRef(null);
  const handleEditProfile = () => {
    navigate('/profile-setup', { state: { editMode: true } });
  };

  // タブ状態（現在のパスに基づいて設定）
  const [activeTab, setActiveTab] = useState(() => {
    if (location.pathname === '/comment-summary' || location.pathname === '/poster-summary') return 'summary';
    if (location.pathname === '/list' || location.pathname === '/poster-list') return 'list';
    if (location.pathname === '/top') return 'poster';
    return 'list';
  });
  
  // 一覧フィルタを sessionStorage から復元するまで API を走らせない（初期値での上書き防止）
  const [listFiltersHydrated, setListFiltersHydrated] = useState(false);

  // フィルター状態
  const [activeFilter, setActiveFilter] = useState('all');
  
  // ポスターデータ
  const [posters, setPosters] = useState(null);
  // お気に入りポスター
  const [favoritePosters, setFavoritePosters] = useState(null);
  // 最も新しいポスターのID（最左タブ用）
  const [activePosterId, setActivePosterId] = useState(null);
  // お気に入りIDリスト
  const [favoriteIds, setFavoriteIds] = useState([]);
  // モーダル表示制御
  const [showModal, setShowModal] = useState(false);
  const [modalPosterId, setModalPosterId] = useState(null);
  const [modalContext, setModalContext] = useState(''); // 'favorite' or 'connection'
  // タグ展開状態を管理（posterId -> boolean）
  const [expandedTags, setExpandedTags] = useState({});
  const [listPage, setListPage] = useState(1);
  const [listTotalPages, setListTotalPages] = useState(0);
  const [listTotalCount, setListTotalCount] = useState(0);
  /** ページ切り替えなど一覧再取得中（既存データ表示時はオーバーレイ） */
  const [listPageFetching, setListPageFetching] = useState(false);
  /** サブフィルタ: recent | date_range | tag | phase */
  const [listSubFilter, setListSubFilter] = useState('recent');
  const [appliedDateFrom, setAppliedDateFrom] = useState('');
  const [appliedDateTo, setAppliedDateTo] = useState('');
  const [appliedTagExact, setAppliedTagExact] = useState('');
  const [appliedPhaseTags, setAppliedPhaseTags] = useState([]);
  const [showPeriodModal, setShowPeriodModal] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [showPhaseModal, setShowPhaseModal] = useState(false);
  const [phaseModalDraftSlugs, setPhaseModalDraftSlugs] = useState([]);
  const [periodFromY, setPeriodFromY] = useState('');
  const [periodFromM, setPeriodFromM] = useState('');
  const [periodFromD, setPeriodFromD] = useState('');
  const [periodToY, setPeriodToY] = useState('');
  const [periodToM, setPeriodToM] = useState('');
  const [periodToD, setPeriodToD] = useState('');
  const [periodModalError, setPeriodModalError] = useState('');
  const [tagQueryInput, setTagQueryInput] = useState('');
  const [tagResults, setTagResults] = useState([]);
  const [tagSearchLoading, setTagSearchLoading] = useState(false);
  const [recentTagSuggestions, setRecentTagSuggestions] = useState([]);
  const [recentTagsLoading, setRecentTagsLoading] = useState(false);

  const formatDateYYYYMMDD = (dateStr) => {
    const d = new Date(dateStr);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const mapApiPosterToGridItem = (item, filterKey) => {
    const date = item.created_at ? formatDateYYYYMMDD(item.created_at) : '';
    const base = {
      id: item.posterId,
      posterId: item.posterId,
      imageUrl: item.imageUrl,
      title: item.title || '',
      authorName: item.authorName || '',
      date,
      question: item.question || '',
      tags: item.tags || [],
      phase_tags: item.phase_tags || [],
      public: item.public || false,
      user_id: item.user_id,
      favoriteCount: item.favoriteCount || 0,
      deleted: item.deleted || false,
      is_deleted: item.is_deleted || false,
    };
    if (filterKey === 'favorite') {
      return {
        ...base,
        authorIconUrl: item.authorIconUrl,
        liked: true,
      };
    }
    if (filterKey === 'my') {
      return {
        ...base,
        authorIconUrl: userProfile?.iconURL || userProfile?.photoURL,
        liked: false,
        user_id: item.user_id || userProfile?.uid || userProfile?.id || userProfile?.user_id,
      };
    }
    const fromFavorite = item.list_origin === 'favorite';
    return {
      ...base,
      authorIconUrl: fromFavorite
        ? (item.authorIconUrl || '')
        : (userProfile?.iconURL || userProfile?.photoURL || '/img/icon.png'),
      liked: fromFavorite,
      user_id: item.user_id || userProfile?.uid || userProfile?.id || userProfile?.user_id,
    };
  };

  /** ページ切替時は先頭へ（ページネーションは画面下部のため） */
  const goToListPage = (page) => {
    scrollWindowToTop();
    setListPage(page);
  };

  const fetchPosterListPage = async () => {
    setListPageFetching(true);
    try {
      if (!sessionId) {
        if (activeFilter === 'favorite') {
          setFavoritePosters([]);
        } else {
          setPosters([]);
        }
        setListTotalPages(0);
        setListTotalCount(0);
        return;
      }
      const listFilter = activeFilter === 'all' ? 'all' : activeFilter === 'my' ? 'my' : 'favorite';
      const pagedParams = {
        list_filter: listFilter,
        page: listPage,
        page_size: LIST_PAGE_SIZE,
        filter_mode: listSubFilter,
      };
      if (listSubFilter === 'date_range' && appliedDateFrom && appliedDateTo) {
        pagedParams.date_from = appliedDateFrom;
        pagedParams.date_to = appliedDateTo;
      }
      if (listSubFilter === 'tag' && appliedTagExact) {
        pagedParams.tag_exact = appliedTagExact;
      }
      if (listSubFilter === 'phase' && appliedPhaseTags.length > 0) {
        pagedParams.phase_tags_any = appliedPhaseTags;
      }
      const res = await getPosterListPaged(sessionId, pagedParams);
      setListTotalPages(res.total_pages);
      setListTotalCount(res.total_count);
      const raw = res.posters || [];
      const filtered = raw.filter(
        (item) =>
          !item.deleted &&
          !item.is_deleted &&
          item.delete_flg !== 1 &&
          item.imageUrl &&
          item.title &&
          item.posterId &&
          (listFilter !== 'favorite' || (item.authorName && item.authorIconUrl != null))
      );
      const mapped = filtered.map((item) => mapApiPosterToGridItem(item, listFilter));
      if (activeFilter === 'favorite') {
        setFavoritePosters(mapped);
      } else {
        setPosters(mapped);
      }
    } catch (err) {
      console.error('ポスター一覧取得失敗:', err);
      if (activeFilter === 'favorite') {
        setFavoritePosters([]);
      } else {
        setPosters([]);
      }
      setListTotalPages(0);
      setListTotalCount(0);
      if (isSessionExpiredError(err)) {
        handleSessionExpired();
      }
    } finally {
      setListPageFetching(false);
    }
  };

  /** sessionStorage に保存した一覧の絞り込み状態を復元（ポスター詳細から戻ってもタブ単位で維持） */
  useEffect(() => {
    const uid = currentUser?.uid;
    if (!uid) {
      setListFiltersHydrated(true);
      return;
    }
    if (listFiltersRestoreDoneUidRef.current === uid) return;
    listFiltersRestoreDoneUidRef.current = uid;
    const s = loadPosterListFiltersFromSession(uid);
    if (s) {
      setActiveFilter(s.activeFilter);
      setListSubFilter(s.listSubFilter);
      setAppliedDateFrom(s.appliedDateFrom);
      setAppliedDateTo(s.appliedDateTo);
      const [fy, fm, fd] = splitYmdParts(s.appliedDateFrom);
      const [ty, tm, td] = splitYmdParts(s.appliedDateTo);
      setPeriodFromY(fy);
      setPeriodFromM(fm);
      setPeriodFromD(fd);
      setPeriodToY(ty);
      setPeriodToM(tm);
      setPeriodToD(td);
      setAppliedTagExact(s.appliedTagExact);
      setAppliedPhaseTags(s.appliedPhaseTags);
      setListPage(s.listPage);
    }
    setListFiltersHydrated(true);
  }, [currentUser?.uid]);

  /** 一覧のフィルタ状態を sessionStorage に永続（ユーザー切替時は load 側の userId で弾く） */
  useEffect(() => {
    const uid = currentUser?.uid;
    if (!listFiltersHydrated || !sessionId || !uid) return;
    persistPosterListFiltersToSession(uid, {
      activeFilter,
      listSubFilter,
      appliedDateFrom,
      appliedDateTo,
      appliedTagExact,
      appliedPhaseTags,
      listPage,
    });
  }, [
    listFiltersHydrated,
    sessionId,
    currentUser?.uid,
    activeFilter,
    listSubFilter,
    appliedDateFrom,
    appliedDateTo,
    appliedTagExact,
    appliedPhaseTags,
    listPage,
  ]);

  useEffect(() => {
    if (!sessionId && typeof sessionStorage !== 'undefined') {
      try {
        sessionStorage.removeItem(POSTER_LIST_SESSION_KEY);
      } catch (_) {
        /* ignore */
      }
    }
  }, [sessionId]);

  useEffect(() => {
    if (!listFiltersHydrated) return;
    fetchPosterListPage();
  }, [
    listFiltersHydrated,
    userProfile,
    activeFilter,
    sessionId,
    listPage,
    listSubFilter,
    appliedDateFrom,
    appliedDateTo,
    appliedTagExact,
    appliedPhaseTags,
  ]);

  useEffect(() => {
    if (!showPeriodModal) {
      setPeriodModalError('');
      return;
    }
    setPeriodModalError('');
  }, [periodFromY, periodFromM, periodFromD, periodToY, periodToM, periodToD, showPeriodModal]);

  // お気に入りIDリストは常に取得
  useEffect(() => {
    const fetchFavoriteIds = async () => {
      if (!sessionId) {
        setFavoriteIds([]);
        return;
      }
      try {
        const favData = await getFavoritePosters(sessionId);
        setFavoriteIds(favData.map(item => item.posterId));
      } catch (err) {
        console.error('お気に入りID取得失敗:', err);
        setFavoriteIds([]);
        
        if (isSessionExpiredError(err)) {
          handleSessionExpired();
        }
      }
    };
    fetchFavoriteIds();
  }, [handleSessionExpired, isSessionExpiredError, sessionId]);

  useEffect(() => {
    if (!showTagModal || !sessionId) {
      if (!showTagModal) {
        setRecentTagSuggestions([]);
        setRecentTagsLoading(false);
      }
      return;
    }
    let cancelled = false;
    const listFilter =
      activeFilter === 'all' ? 'all' : activeFilter === 'my' ? 'my' : 'favorite';
    (async () => {
      setRecentTagsLoading(true);
      try {
        const tags = await getRecentPosterDocTags(sessionId, { list_filter: listFilter });
        if (!cancelled) {
          setRecentTagSuggestions(tags);
        }
      } catch (err) {
        console.error('直近タグ取得失敗:', err);
        if (!cancelled) {
          setRecentTagSuggestions([]);
        }
        if (isSessionExpiredError(err)) {
          handleSessionExpired();
        }
      } finally {
        if (!cancelled) {
          setRecentTagsLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showTagModal, sessionId, activeFilter, handleSessionExpired, isSessionExpiredError]);

  // 質問リスト（モック）
  const [questions] = useState([
    { id: 1, text: '最近仕事はどうですか？' },
    { id: 2, text: '最近何か読みましたか？' }
  ]);

  // タブ変更ハンドラー
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'poster') {
      clearPosterListScrollPosition();
      navigate('/top');
    }
    if (tab === 'list') {
      navigate('/list');
    }
    if (tab === 'summary') {
      clearPosterListScrollPosition();
      navigate('/comment-summary');
    }
  };

  // フィルター変更ハンドラー
  const handleFilterChange = (filter) => {
    // 同じフィルターが選択された場合は何もしない
    if (filter === activeFilter) {
      return;
    }
    
    // フィルター変更時にリストを一時クリアしてローディング表示を確実に出す
    setActiveFilter(filter);
    setListPage(1);
    setListSubFilter('recent');
    setAppliedDateFrom('');
    setAppliedDateTo('');
    setAppliedTagExact('');
    setAppliedPhaseTags([]);
    if (filter === 'favorite') {
      setFavoritePosters(null); // ローディング用
    } else if (filter === 'all' || filter === 'my') {
      setPosters(null); // ローディング用
    }
    // フィルター変更完了
  };

  // いいねトグル
  const handleLikeToggle = (posterId) => {
    setPosters((prevPosters) =>
      (prevPosters || []).map((poster) =>
        poster.id === posterId ? { ...poster, liked: !poster.liked } : poster
      )
    );
    // いいねトグル完了
  };

  // ポスター詳細へ遷移
  const handlePosterClick = (poster) => {
    navigate('/poster-detail', {
      state: {
        posterImageUrl: poster.imageUrl,
        title: poster.title,
        posterId: poster.posterId || poster.id, // posterId優先、なければid
        authorIconUrl: poster.authorIconUrl,
        authorName: poster.authorName,
        question: poster.question || '',
        phase_tags: poster.phase_tags || []
      }
    });
  };

  // 質問クリック
  const handleQuestionClick = (question) => {
    navigate('/poster-create', {
      state: {
        question: question
      }
    });
  };

  // 画像読み込みエラーハンドラ
  const handleImageError = (e) => {
    console.warn('画像読み込み失敗:', e.target.src);
    
    // 削除されたポスターの可能性が高い場合は要素全体を非表示
    if (e.target.src.includes('deleted') || e.target.src.includes('404')) {
      const gridItem = e.target.closest('.list-grid-item');
      if (gridItem) {
        gridItem.style.display = 'none';
        return;
      }
    }
    
    // 通常のエラーの場合はプレースホルダー表示
    e.target.src = '/img/placeholder.png';
    e.target.style.filter = 'grayscale(100%)';
    e.target.style.opacity = '0.5';
    e.target.onerror = null; // 無限ループ防止
  };

  // お気に入り解除モーダル表示
  const handleFavoriteClick = (posterId, context) => {
    setModalPosterId(posterId);
    setModalContext(context);
    setShowModal(true);
  };

  // お気に入り解除処理
  const handleRemoveFavorite = async () => {
    const posterId = modalPosterId;
    const context = modalContext;
    setShowModal(false);
    if (!posterId) return;
    if (!sessionId) return;
    const success = await deleteFavoritePoster(sessionId, posterId);
    if (success) {
      if (context === 'favorite') {
        setFavoritePosters(prev => prev.filter(p => p.id !== posterId));
        setFavoriteIds(prev => prev.filter(id => id !== posterId));
      }
      // お気に入りキャッシュをクリア
      localStorage.removeItem(`favoritePosters_${sessionId}`);
      localStorage.removeItem(`favoritePosters_${sessionId}_timestamp`);
    }
  };

  const openPeriodModal = () => {
    setPeriodModalError('');
    if (appliedDateFrom && appliedDateTo) {
      const [fy, fm, fd] = appliedDateFrom.split('-');
      const [ty, tm, td] = appliedDateTo.split('-');
      setPeriodFromY(fy);
      setPeriodFromM(fm);
      setPeriodFromD(fd);
      setPeriodToY(ty);
      setPeriodToM(tm);
      setPeriodToD(td);
    } else {
      const n = new Date();
      setPeriodFromY(String(n.getFullYear()));
      setPeriodFromM(String(n.getMonth() + 1).padStart(2, '0'));
      setPeriodFromD('01');
      setPeriodToY(String(n.getFullYear()));
      setPeriodToM(String(n.getMonth() + 1).padStart(2, '0'));
      setPeriodToD(String(n.getDate()).padStart(2, '0'));
    }
    setShowPeriodModal(true);
  };

  const handlePeriodConfirm = () => {
    const fromStr = toYYYYMMDD(periodFromY, periodFromM, periodFromD);
    const toStr = toYYYYMMDD(periodToY, periodToM, periodToD);
    if (!fromStr || !toStr) {
      setPeriodModalError('年・月・日を正しく入力してください。');
      return;
    }
    if (fromStr > toStr) {
      setPeriodModalError('開始日は終了日以前にしてください。');
      return;
    }
    setPeriodModalError('');
    setAppliedDateFrom(fromStr);
    setAppliedDateTo(toStr);
    setAppliedPhaseTags([]);
    setListSubFilter('date_range');
    setShowPeriodModal(false);
    setListPage(1);
  };

  const handleSubFilterRecent = (e) => {
    e.preventDefault();
    setListSubFilter('recent');
    setAppliedDateFrom('');
    setAppliedDateTo('');
    setAppliedTagExact('');
    setAppliedPhaseTags([]);
    setListPage(1);
  };

  const openPhaseModal = () => {
    setPhaseModalDraftSlugs([...appliedPhaseTags]);
    setShowPhaseModal(true);
  };

  const togglePhaseModalSlug = (slug) => {
    setPhaseModalDraftSlugs((prev) =>
      prev.includes(slug) ? prev.filter((x) => x !== slug) : [...prev, slug]
    );
  };

  const handlePhaseModalConfirm = () => {
    const ordered = orderPhaseSlugs(phaseModalDraftSlugs);
    if (ordered.length === 0) {
      setAppliedPhaseTags([]);
      setListSubFilter('recent');
    } else {
      setAppliedTagExact('');
      setAppliedDateFrom('');
      setAppliedDateTo('');
      setAppliedPhaseTags(ordered);
      setListSubFilter('phase');
    }
    setShowPhaseModal(false);
    setListPage(1);
  };

  const openTagModal = () => {
    setTagQueryInput('');
    setTagResults([]);
    setShowTagModal(true);
  };

  const handleTagSearchSubmit = async (e) => {
    e.preventDefault();
    if (!sessionId) return;
    const q = tagQueryInput.trim();
    if (!q) {
      setTagResults([]);
      return;
    }
    setTagSearchLoading(true);
    try {
      const listFilter =
        activeFilter === 'all' ? 'all' : activeFilter === 'my' ? 'my' : 'favorite';
      const tags = await searchPosterDocTags(sessionId, { list_filter: listFilter, q });
      setTagResults(tags);
    } catch (err) {
      console.error('タグ検索失敗:', err);
      setTagResults([]);
      if (isSessionExpiredError(err)) {
        handleSessionExpired();
      }
    } finally {
      setTagSearchLoading(false);
    }
  };

  const handleSelectTagFromSearch = (tag) => {
    setAppliedTagExact(tag);
    setAppliedPhaseTags([]);
    setListSubFilter('tag');
    setShowTagModal(false);
    setListPage(1);
  };

  // タグ展開のハンドラ（「T」エリアをクリックした時）
  const handleTagExpand = (posterId, e) => {
    if (e) {
      e.stopPropagation(); // ポスタークリックイベントを防ぐ
    }
    setExpandedTags(prev => ({
      ...prev,
      [posterId]: true
    }));
  };

  // タグ折りたたみのハンドラ（大なり記号をクリックした時）
  const handleTagCollapse = (posterId, e) => {
    e.stopPropagation(); // ポスタークリックイベントを防ぐ
    setExpandedTags(prev => ({
      ...prev,
      [posterId]: false
    }));
  };

  return (
    <div className="list-container">
      {/* ヘッダー */}
      <div className="top-header">
        <div className="user-info">
          <img
            src={userProfile?.iconURL || userProfile?.photoURL || '/img/icon.png'}
            alt="プロフィール画像"
            title="プロフィール編集"
            onClick={handleEditProfile}
            style={{cursor: 'pointer'}}
            onError={handleImageError}
          />
          <span className="user-name">{userProfile?.displayName || userProfile?.nickname || 'ユーザー'} さん</span>
          {userProfile?.world && <span className="user-world">{userProfile.world}</span>}
        </div>
        <div className="header-icons">
          <img
            src="/img/graph.png"
            alt="みんなのサマリー"
            title="みんなのサマリー"
            className="header-icon"
            onClick={() => navigate('/')}
            style={{cursor: 'pointer'}}
            onError={handleImageError}
          />
          <img 
            src="/img/logout.png"
            alt="ログアウト"
            title="ログアウト"
            className="header-icon"
            onClick={() => { if (window.confirm('ログアウトしますか？')) logout(); }}
            style={{cursor: 'pointer', marginLeft: '12px'}} 
            onError={handleImageError}
          />
        </div>
      </div>

      {/* タブメニュー */}
      <div className="list-tab-menu-wide">
        <button 
          className={`list-tab-wide ${activeTab === 'poster' ? 'active' : ''}`}
          onClick={() => handleTabChange('poster')}
        >
          ポスターを作る
        </button>
        <button 
          className={`list-tab-wide ${activeTab === 'list' ? 'active' : ''}`}
          onClick={() => handleTabChange('list')}
        >
          ポスター一覧
        </button>
        <button
          type="button"
          className={`list-tab-wide ${activeTab === 'summary' ? 'active' : ''}`}
          onClick={() => handleTabChange('summary')}
        >
          サマリー
        </button>
      </div>

      {/* ナビゲーション（フィルター） */}
      <div className="list-navigation">
        <a 
          href="#"
          className={activeFilter === 'all' ? 'active' : ''}
          onClick={(e) => {
            e.preventDefault();
            handleFilterChange('all');
          }}
        >
          全て
        </a>
        <a 
          href="#"
          className={activeFilter === 'my' ? 'active' : ''}
          onClick={(e) => {
            e.preventDefault();
            handleFilterChange('my');
          }}
        >
          自分の投稿
        </a>
        <a 
          href="#"
          className={activeFilter === 'favorite' ? 'active' : ''}
          onClick={(e) => {
            e.preventDefault();
            handleFilterChange('favorite');
          }}
        >
          お気に入り
        </a>
      </div>

      <div className="list-sub-filter-divider" role="presentation" />

      <div className="list-navigation list-sub-navigation">
        <a
          href="#"
          className={listSubFilter === 'recent' ? 'active' : ''}
          onClick={(e) => {
            e.preventDefault();
            handleSubFilterRecent(e);
          }}
        >
          直近の投稿
        </a>
        <a
          href="#"
          className={listSubFilter === 'date_range' ? 'active' : ''}
          onClick={(e) => {
            e.preventDefault();
            openPeriodModal();
          }}
        >
          期間指定
        </a>
        <a
          href="#"
          className={listSubFilter === 'tag' ? 'active' : ''}
          onClick={(e) => {
            e.preventDefault();
            openTagModal();
          }}
        >
          タグ検索
        </a>
        <a
          href="#"
          className={listSubFilter === 'phase' ? 'active' : ''}
          onClick={(e) => {
            e.preventDefault();
            openPhaseModal();
          }}
        >
          思考フェーズ
        </a>
      </div>

      {/* ローディング中表示 */}
      {(
        (activeFilter === 'favorite' && favoritePosters === null) ||
        (activeFilter !== 'favorite' && posters === null)
      ) && (
        <div className="loading">
          <div className="loading-spinner">
            <img src="/img/cat.png" alt="loading cat" className="loading-cat" />
            <div className="loading-text">ロード中・・</div>
          </div>
        </div>
      )}

      {/* モーダル */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <p>お気に入り解除しますか？</p>
            <button onClick={handleRemoveFavorite}>OK</button>
            <button onClick={() => setShowModal(false)}>キャンセル</button>
          </div>
        </div>
      )}

      {showPeriodModal && (
        <div
          className="modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setPeriodModalError('');
              setShowPeriodModal(false);
            }
          }}
        >
          <div
            className="poster-list-filter-modal poster-list-filter-modal-period"
            onClick={(ev) => ev.stopPropagation()}
          >
            <div className="poster-list-filter-modal-breadcrumb">
              {filterBreadcrumbLabel(activeFilter)} &gt; 期間指定
            </div>
            <div className="poster-list-period-row">
              <div className="poster-list-period-fields">
                <input
                  type="text"
                  inputMode="numeric"
                  className="poster-list-ymd poster-list-ymd-year"
                  value={periodFromY}
                  onChange={(e) => setPeriodFromY(e.target.value)}
                  aria-label="開始年"
                />
                <span className="poster-list-ymd-label">年</span>
                <input
                  type="text"
                  inputMode="numeric"
                  className="poster-list-ymd"
                  value={periodFromM}
                  onChange={(e) => setPeriodFromM(e.target.value)}
                  aria-label="開始月"
                />
                <span className="poster-list-ymd-label">月</span>
                <input
                  type="text"
                  inputMode="numeric"
                  className="poster-list-ymd"
                  value={periodFromD}
                  onChange={(e) => setPeriodFromD(e.target.value)}
                  aria-label="開始日"
                />
                <span className="poster-list-ymd-label">日</span>
              </div>
              <span className="poster-list-period-suffix">から</span>
            </div>
            <div className="poster-list-period-row">
              <div className="poster-list-period-fields">
                <input
                  type="text"
                  inputMode="numeric"
                  className="poster-list-ymd poster-list-ymd-year"
                  value={periodToY}
                  onChange={(e) => setPeriodToY(e.target.value)}
                  aria-label="終了年"
                />
                <span className="poster-list-ymd-label">年</span>
                <input
                  type="text"
                  inputMode="numeric"
                  className="poster-list-ymd"
                  value={periodToM}
                  onChange={(e) => setPeriodToM(e.target.value)}
                  aria-label="終了月"
                />
                <span className="poster-list-ymd-label">月</span>
                <input
                  type="text"
                  inputMode="numeric"
                  className="poster-list-ymd"
                  value={periodToD}
                  onChange={(e) => setPeriodToD(e.target.value)}
                  aria-label="終了日"
                />
                <span className="poster-list-ymd-label">日</span>
              </div>
              <span className="poster-list-period-suffix">まで</span>
            </div>
            <div
              className="poster-list-period-modal-error"
              role={periodModalError ? 'alert' : undefined}
              aria-live="polite"
            >
              {periodModalError}
            </div>
            <div className="poster-list-filter-modal-actions">
              <button type="button" className="poster-list-filter-primary" onClick={handlePeriodConfirm}>
                決定
              </button>
              <button
                type="button"
                className="poster-list-filter-secondary"
                onClick={() => {
                  setPeriodModalError('');
                  setShowPeriodModal(false);
                }}
              >
                キャンセル
              </button>
            </div>
          </div>
        </div>
      )}

      {showTagModal && (
        <div
          className="modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowTagModal(false);
          }}
        >
          <div className="poster-list-filter-modal poster-list-tag-modal" onClick={(ev) => ev.stopPropagation()}>
            <div className="poster-list-filter-modal-breadcrumb">
              {filterBreadcrumbLabel(activeFilter)} &gt; タグ検索
            </div>
            <div className="poster-list-tag-recent-block">
              {recentTagsLoading && (
                <div className="poster-list-tag-recent-loading">直近のタグを読み込み中…</div>
              )}
              {!recentTagsLoading && recentTagSuggestions.length > 0 && (
                <>
                  <div className="poster-list-tag-recent-title">直近のタグ</div>
                  <ul className="poster-list-tag-recent-list" aria-label="直近のタグ">
                    {recentTagSuggestions.map((tag) => (
                      <li key={tag}>
                        <button
                          type="button"
                          className="poster-list-tag-link"
                          onClick={() => handleSelectTagFromSearch(tag)}
                        >
                          {tag}
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
            <form className="poster-list-tag-search-form" onSubmit={handleTagSearchSubmit}>
              <div className="poster-list-tag-search-row">
                <input
                  type="text"
                  className="poster-list-tag-search-input"
                  value={tagQueryInput}
                  onChange={(e) => setTagQueryInput(e.target.value)}
                  placeholder="キーワード"
                  aria-label="タグ検索キーワード"
                />
                <button type="submit" className="poster-list-filter-primary poster-list-tag-search-btn" disabled={tagSearchLoading}>
                  検索
                </button>
              </div>
            </form>
            {tagSearchLoading && <div className="poster-list-tag-loading">検索中…</div>}
            <ul className="poster-list-tag-results">
              {tagResults.map((tag) => (
                <li key={tag}>
                  <button type="button" className="poster-list-tag-link" onClick={() => handleSelectTagFromSearch(tag)}>
                    {tag}
                  </button>
                </li>
              ))}
            </ul>
            <div className="poster-list-filter-modal-actions">
              <button type="button" className="poster-list-filter-secondary" onClick={() => setShowTagModal(false)}>
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}

      {showPhaseModal && (
        <div
          className="modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowPhaseModal(false);
          }}
        >
          <div
            className="poster-list-filter-modal poster-list-phase-modal"
            onClick={(ev) => ev.stopPropagation()}
          >
            <div className="poster-list-filter-modal-breadcrumb">
              {filterBreadcrumbLabel(activeFilter)} &gt; 思考フェーズ
            </div>
            <p className="poster-list-phase-modal-hint">タップして選択（複数可）。いずれかのフェーズが付いたコメントを持つポスターを OR で絞り込みます。</p>
            <div className="poster-list-phase-grid" role="group" aria-label="思考フェーズの選択">
              {PHASE_FILTER_TAGS.map((slug) => {
                const selected = phaseModalDraftSlugs.includes(slug);
                return (
                  <button
                    key={slug}
                    type="button"
                    className={`poster-list-phase-chip${selected ? ' selected' : ''}`}
                    onClick={() => togglePhaseModalSlug(slug)}
                    aria-pressed={selected}
                    title={slug}
                  >
                    <img
                      src={`/img/phase-tags/${slug}.png`}
                      alt=""
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </button>
                );
              })}
            </div>
            <div className="poster-list-filter-modal-actions">
              <button type="button" className="poster-list-filter-primary" onClick={handlePhaseModalConfirm}>
                決定
              </button>
              <button
                type="button"
                className="poster-list-filter-secondary"
                onClick={() => setShowPhaseModal(false)}
              >
                キャンセル
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ポスター一覧 */}
      <div
        className="list-grid-container"
        aria-busy={listPageFetching ? 'true' : 'false'}
      >
        {listPageFetching &&
          ((activeFilter === 'favorite' && favoritePosters !== null) ||
            (activeFilter !== 'favorite' && posters !== null)) && (
            <div className="list-grid-loading-overlay" aria-live="polite">
              <div className="list-grid-loading-inner">
                <img src="/img/cat.png" alt="" className="loading-cat" />
                <div className="loading-text">読み込み中…</div>
              </div>
            </div>
          )}
        {activeFilter === 'favorite'
          ? favoritePosters && favoritePosters.map((poster) => (
              <div key={poster.id} className="list-grid-item" onClick={() => handlePosterClick(poster)}>
                <div className="list-poster-wrapper">
                  <img 
                    src={poster.imageUrl} 
                    alt="ポスター" 
                    className="list-poster-image"
                    loading="lazy"
                    decoding="async"
                    onError={handleImageError}
                  />
                  <img
                    src={poster.authorIconUrl || ''}
                    alt={'作者アイコン'}
                    className="list-kraken-icon"
                    style={{ opacity: 0.85 }}
                    onError={(e) => e.target.style.display = 'none'}
                  />
                  {poster.phase_tags && poster.phase_tags.length > 0 && (
                    <div 
                      className={`list-tag-badge ${expandedTags[poster.posterId || poster.id] ? 'expanded' : ''}`}
                      onClick={!expandedTags[poster.posterId || poster.id] ? (e) => handleTagExpand(poster.posterId || poster.id, e) : (e) => e.stopPropagation()}
                    >
                      {expandedTags[poster.posterId || poster.id] ? (
                        <>
                          <div 
                            className="list-tag-images"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {poster.phase_tags.map((tagItem, idx) => (
                              <img
                                key={idx}
                                src={`/img/phase-tags/${tagItem.tag}.png`}
                                alt={tagItem.tag}
                                title={tagItem.tag}
                                className="list-tag-image"
                                onError={(e) => e.target.style.display = 'none'}
                              />
                            ))}
                          </div>
                          <span 
                            className="list-tag-collapse"
                            onClick={(e) => handleTagCollapse(poster.posterId || poster.id, e)}
                          >&#62;</span>
                        </>
                      ) : (
                        <span className="list-tag-letter">T</span>
                      )}
                    </div>
                  )}
                </div>
                <div className="list-date">
                  {poster.date}
                  <span>
                    <img
                      src={"/img/like_red.png"}
                      alt={"いいね済み"}
                      onClick={e => {
                        e.stopPropagation();
                        handleFavoriteClick(poster.posterId || poster.id, 'favorite');
                      }}
                      style={{ cursor: 'pointer' }}
                    />
                  </span>
                </div>
              </div>
            ))
          : posters && posters.map((poster) => {
              return (
                <div key={poster.id} className="list-grid-item" onClick={() => handlePosterClick(poster)}>
                  <div className="list-poster-wrapper">
                    <img 
                      src={poster.imageUrl} 
                      alt="ポスター" 
                      className="list-poster-image" 
                      loading="lazy"
                      decoding="async"
                      onError={handleImageError}
                    />
                    <img 
                      src={poster.authorIconUrl}
                      alt={poster.authorName}
                      className="list-kraken-icon"
                      style={{ opacity: 0.85 }}
                      onError={handleImageError}
                    />
                    {poster.phase_tags && poster.phase_tags.length > 0 && (
                      <div 
                        className={`list-tag-badge ${expandedTags[poster.posterId || poster.id] ? 'expanded' : ''}`}
                        onClick={!expandedTags[poster.posterId || poster.id] ? (e) => handleTagExpand(poster.posterId || poster.id, e) : (e) => e.stopPropagation()}
                      >
                        {expandedTags[poster.posterId || poster.id] ? (
                          <>
                            <div 
                              className="list-tag-images"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {poster.phase_tags.map((tagItem, idx) => (
                                <img
                                  key={idx}
                                  src={`/img/phase-tags/${tagItem.tag}.png`}
                                  alt={tagItem.tag}
                                  title={tagItem.tag}
                                  className="list-tag-image"
                                  onError={(e) => e.target.style.display = 'none'}
                                />
                              ))}
                            </div>
                            <span 
                              className="list-tag-collapse"
                              onClick={(e) => handleTagCollapse(poster.posterId || poster.id, e)}
                            >&#62;</span>
                          </>
                        ) : (
                          <span className="list-tag-letter">T</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="list-date">
                    {poster.date}
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      <img 
                        src="/img/like_purple.png"
                        alt="いいねの数"
                        style={{ marginRight: '4px' }}
                      />
                      <span style={{ fontWeight: 'bold', color: '#824394', fontSize: '13px', minWidth: '18px', textAlign: 'center' }}>{poster.favoriteCount || 0}</span>
                    </span>
                  </div>
                </div>
              );
            })}
      </div>

      {listTotalCount > 0 && listTotalPages > 0 && (
        <nav className="list-pagination" aria-label="ポスター一覧のページ">
          <button
            type="button"
            className="list-pagination-prev"
            disabled={listPage <= 1 || listPageFetching}
            onClick={() => goToListPage(Math.max(1, listPage - 1))}
          >
            前へ
          </button>
          <div className="list-pagination-pages">
            {Array.from({ length: listTotalPages }, (_, i) => i + 1).map((num) => (
              <button
                key={num}
                type="button"
                className={`list-pagination-page${num === listPage ? ' list-pagination-page-active' : ''}`}
                disabled={listPageFetching}
                onClick={() => goToListPage(num)}
                aria-current={num === listPage ? 'page' : undefined}
              >
                {num}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="list-pagination-next"
            disabled={listPage >= listTotalPages || listPageFetching}
            onClick={() => goToListPage(Math.min(listTotalPages, listPage + 1))}
          >
            次へ
          </button>
        </nav>
      )}
    </div>
  );
};

export default PosterList;
